import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

// material-ui
import { styled, useTheme } from "@mui/material/styles";
import {
    AppBar,
    Box,
    CssBaseline,
    Toolbar,
    Alert,
    Snackbar,
    Grow
} from "@mui/material";

// project imports
import Breadcrumbs from "ui-component/extended/Breadcrumbs";
import Header from "./Header";
import Sidebar from "./Sidebar";
import Customization from "../Customization";
import navigation from "menu-items";

import SweetAlert from "react-bootstrap-sweetalert";
import { closeToaster, closeMessage, setUserToken, showSessionTimeout, setDrawerOpen as setDrawerOpenShared } from "store/Shared.Slice";

// assets
import { IconChevronRight } from "@tabler/icons";
import { useNavigate, useLocation } from "react-router-dom";
import jwt_decode from "jwt-decode";
import { attemptTokenRefresh } from "utils/tokenRefresh";
import { useTranslation } from "react-i18next";
import { useRestoreScrollYPosition } from "hooks/useRestoreScrollYPosition";
import { useGetSubscriptionInfoQuery } from "Services/OrganizationProfileService";
import WelcomeMessage from "views/welcome-message";

// styles
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
    ({ theme, open, overflowHidden }) => ({
        ...theme.typography.mainContent,
        ...(!open && {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            transition: theme.transitions.create("margin", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            [theme.breakpoints.up("md")]: {
            },
            [theme.breakpoints.down("md")]: {
                marginLeft: "20px",
                padding: "16px",
            },
            [theme.breakpoints.down("sm")]: {
                marginLeft: "10px",
                padding: "16px",
                marginRight: "10px",
            },
        }),
        ...(open && {
            transition: theme.transitions.create("margin", {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
            marginLeft: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            [theme.breakpoints.down("md")]: {
                marginLeft: "20px",
            },
            [theme.breakpoints.down("sm")]: {
                marginLeft: "10px",
            },
        }),
        overflow: overflowHidden ? "hidden" : "unset",
    })
);

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const sessionId = useRef(uuidv4());
    const location = useLocation();
    const isInFoundationPage = location.pathname.includes("foundation-profile");

    const { isLoading: isLoadingInfo } = useGetSubscriptionInfoQuery(sessionId.current);

    // Handle left drawer
    const leftDrawerOpened = useSelector((state) => state.customization.opened);
    const { message, toaster, previewStagingEnabled } = useSelector((state) => state.sharedState);
    const [ drawerOpen, setDrawerOpen ] = useState(localStorage.getItem('drawerOpen') === 'true');

    const dispatch = useDispatch();
    const navigate = useNavigate();

    useRestoreScrollYPosition();

    const handleToken = useCallback(async () => {
        const token = localStorage.getItem('token');

        if (token == null) {
            navigate('/');
            return;
        }

        try {
            const userToken = jwt_decode(token);
            const secondsUntilExpiry = userToken.exp - Date.now() / 1000;

            if (secondsUntilExpiry < 300) {
                const refreshed = await attemptTokenRefresh();
                if (refreshed) {
                    dispatch(setUserToken(jwt_decode(localStorage.getItem('token'))));
                    window.location.reload();
                    return;
                }
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                dispatch(showSessionTimeout());
                navigate('/');
                return;
            }
            dispatch(setUserToken(userToken));
        }
        catch (err) {
            console.log(err);
            dispatch(showSessionTimeout());
            navigate('/');
        }

    }, [dispatch, navigate]);

    const handleDrawerOpen = (isOpen) => {
        setDrawerOpen(isOpen);
        localStorage.setItem('drawerOpen', isOpen);
    }

    useEffect(handleToken, [handleToken]);

    useEffect(() => {
        const interval = setInterval(handleToken, 1 * 60 * 1000);
        return () => clearInterval(interval);
    }, [handleToken]);

    useEffect(() => {
        dispatch(setDrawerOpenShared(drawerOpen));
    }, [dispatch, drawerOpen]);

    return (
        <>
            <Box sx={{ display: "flex" }}>
                <CssBaseline />
                {/* header */}
                <AppBar
                    enableColorOnDark
                    position="fixed"
                    color="inherit"
                    elevation={0}
                    sx={{
                        bgcolor: theme.palette.background.default,
                        transition: leftDrawerOpened
                            ? theme.transitions.create("width")
                            : "none",
                    }}
                >
                    <Toolbar>
                        <Header />
                    </Toolbar>
                </AppBar>

                {/* Toaster utility */}
                {toaster.isOpen &&
                    <Snackbar
                        open={toaster.isOpen}
                        autoHideDuration={3000}
                        onClose={() => dispatch(closeToaster())}
                        anchorOrigin={toaster.anchorOrigin ?? { vertical: 'bottom', horizontal: 'center' }}
                        {...toaster.withTransition && { TransitionComponent: (props) => <Grow { ...props } /> } }
                        disableWindowBlurListener={true}
                        { ...toaster.adjustedCenter && drawerOpen && { sx: { marginLeft: '140px' } } }
                    >
                        <Alert onClose={() => dispatch(closeToaster())} severity={toaster.severity} sx={{ width: '100%' }} variant="filled">
                            {toaster.format !== '' ? t(toaster.message, toaster.format) : t(toaster.message)}
                        </Alert>
                    </Snackbar>
                }

                {/* Message utility */}
                {message.isOpen && (
                    <SweetAlert
                        title={t(message.title)}
                        onConfirm={() => dispatch(closeMessage())}
                        onCancel={() => dispatch(closeMessage())}
                    >
                        {t(message.text)}
                    </SweetAlert>
                )}

                {/* drawer */}
                <Sidebar open={drawerOpen} onDrawerOpen={handleDrawerOpen} />

                {/* main content */}
                <Main theme={theme} overflowHidden={!isInFoundationPage}>
                    {/* Staging preview indicator */}
                    {previewStagingEnabled && (
                        <Alert severity="warning" variant="filled" sx={{ mb: 2 }}>
                            You're previewing STAGING data — this isn't what other users see.
                        </Alert>
                    )}
                    {/* breadcrumb */}
                    <Breadcrumbs
                        separator={IconChevronRight}
                        navigation={navigation}
                        icon
                        title
                        rightAlign
                    />
                    <Outlet />
                </Main>
                <Customization />
            </Box>
            <WelcomeMessage />
        </>
    );
};

export default MainLayout;
